// src/cockpit4/components/AIProjectAssistant.tsx
// ============================================
// P0-功能3：AI项目助理（项目中心）
//
// 功能：
//   1. 聊天式界面
//   2. 快捷指令按钮（生成计划/风险检查/生成周报/每日站会）
//   3. AI建议卡片展示
//   4. 进度偏差预警（对比计划与实际，识别偏差>10%的任务）
//   5. 自动生成周报（基于任务数据生成 Markdown 周报，写入 Vault）
//   6. 风险自动识别（延期任务、阻塞任务、资源冲突）
//
// 数据来源：
//   - localStorage 读取任务数据（xeaos_tasks_list）
//   - localStorage 读取自建项目（xeaos_projects_custom）
//   - /vault-api/write 写入周报到 Vault
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import {
  Bot,
  Send,
  Sparkles,
  FileText,
  AlertTriangle,
  Loader2,
  ClipboardList,
  User,
  AlertOctagon,
  Activity,
} from "lucide-react";
import {
  loadTasks,
  type Task,
} from "./TaskBoard";
import { loadCustomProjects, type Project } from "./ProjectDashboard";

// ============================================
// 类型定义
// ============================================

/** 建议卡片类型 */
interface AISuggestion {
  type: "risk" | "plan" | "info" | "action";
  title: string;
  detail: string;
  severity: "info" | "warning" | "danger";
}

/** 聊天消息 */
interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  suggestions?: AISuggestion[];
}

// ============================================
// 常量配置
// ============================================

const REPORT_VAULT_PATH = "10_Operations（运营层）/Reports";

const QUICK_COMMANDS = [
  {
    id: "plan",
    label: "生成计划",
    icon: ClipboardList,
    color: "#3B82F6",
  },
  {
    id: "risk",
    label: "风险检查",
    icon: AlertTriangle,
    color: "#F59E0B",
  },
  {
    id: "report",
    label: "生成周报",
    icon: FileText,
    color: "#10B981",
  },
  {
    id: "standup",
    label: "每日站会",
    icon: Activity,
    color: "#A78BFA",
  },
] as const;

// ============================================
// 分析工具函数
// ============================================

/** 计算任务进度偏差（plannedProgress - actualProgress，正值表示落后） */
function getDeviation(task: Task): number {
  return task.plannedProgress - task.actualProgress;
}

/** 识别进度偏差 > 10% 的任务 */
function findDeviationTasks(tasks: Task[]): Task[] {
  return tasks
    .filter((t) => t.status !== "done")
    .filter((t) => getDeviation(t) > 10);
}

/** 识别延期任务（未完成且已过截止日） */
function findOverdueTasks(tasks: Task[]): Task[] {
  const now = Date.now();
  return tasks
    .filter((t) => t.status !== "done")
    .filter((t) => new Date(t.dueDate).getTime() < now);
}

/** 识别阻塞任务 */
function findBlockedTasks(tasks: Task[]): Task[] {
  return tasks.filter((t) => t.status === "blocked");
}

/** 识别资源冲突（同一负责人有 2+ 高优先级未完成任务） */
function findResourceConflicts(tasks: Task[]): Map<string, Task[]> {
  const byAssignee = new Map<string, Task[]>();
  tasks
    .filter(
      (t) =>
        t.status !== "done" &&
        (t.priority === "high" || t.priority === "urgent")
    )
    .forEach((t) => {
      if (!t.assignee) return;
      if (!byAssignee.has(t.assignee)) byAssignee.set(t.assignee, []);
      byAssignee.get(t.assignee)!.push(t);
    });
  // 只保留有 2+ 任务的负责人
  const conflicts = new Map<string, Task[]>();
  byAssignee.forEach((taskList, assignee) => {
    if (taskList.length >= 2) conflicts.set(assignee, taskList);
  });
  return conflicts;
}

/** 生成 Markdown 周报 */
function buildWeeklyReport(tasks: Task[], projects: Project[]): string {
  const date = new Date().toISOString().slice(0, 10);
  const now = new Date().toISOString();

  const done = tasks.filter((t) => t.status === "done");
  const inProgress = tasks.filter((t) => t.status === "in_progress");
  const blocked = tasks.filter((t) => t.status === "blocked");
  const todo = tasks.filter((t) => t.status === "todo");
  const overdue = findOverdueTasks(tasks);
  const deviationTasks = findDeviationTasks(tasks);

  const lines: string[] = [
    "---",
    "type: project_weekly_report",
    `date: ${date}`,
    `total_tasks: ${tasks.length}`,
    `completed: ${done.length}`,
    `in_progress: ${inProgress.length}`,
    `blocked: ${blocked.length}`,
    `created_at: ${now}`,
    "---",
    "",
    `# 项目周报 · ${date}`,
    "",
    `> 自动生成于 ${now}，基于项目中心任务数据`,
    "",
    "## 本周概况",
    "",
    `- 总任务数：${tasks.length}`,
    `- 已完成：${done.length}`,
    `- 进行中：${inProgress.length}`,
    `- 待办：${todo.length}`,
    `- 阻塞：${blocked.length}`,
    `- 延期：${overdue.length}`,
    `- 进度偏差 >10%：${deviationTasks.length}`,
    "",
    "## 项目概览",
    "",
  ];

  if (projects.length > 0) {
    projects.forEach((p) => {
      const projectTasks = tasks.filter((t) => t.projectId === p.id);
      lines.push(
        `- **${p.name}**（负责人：${p.owner}，进度：${p.progress}%）— 关联任务 ${projectTasks.length} 个`
      );
    });
  } else {
    lines.push("- 暂无自建项目");
  }

  lines.push("", "## 已完成任务", "");
  if (done.length > 0) {
    done.forEach((t) => {
      lines.push(
        `- [x] ${t.title}（${t.assignee}，截止 ${t.dueDate}）`
      );
    });
  } else {
    lines.push("- 本周无已完成任务");
  }

  lines.push("", "## 进行中任务", "");
  if (inProgress.length > 0) {
    inProgress.forEach((t) => {
      const dev = getDeviation(t);
      const devStr = dev > 10 ? ` ⚠ 偏差 ${dev}%` : "";
      lines.push(
        `- ${t.title}（${t.assignee}，进度 ${t.actualProgress}%，截止 ${t.dueDate}）${devStr}`
      );
    });
  } else {
    lines.push("- 当前无进行中任务");
  }

  lines.push("", "## 风险预警", "");
  if (overdue.length > 0) {
    lines.push("### 延期任务");
    overdue.forEach((t) => {
      lines.push(`- ${t.title}（${t.assignee}，截止 ${t.dueDate}）`);
    });
    lines.push("");
  }
  if (blocked.length > 0) {
    lines.push("### 阻塞任务");
    blocked.forEach((t) => {
      lines.push(`- ${t.title}（${t.assignee}，截止 ${t.dueDate}）`);
    });
    lines.push("");
  }
  if (deviationTasks.length > 0) {
    lines.push("### 进度偏差 >10%");
    deviationTasks.forEach((t) => {
      lines.push(
        `- ${t.title}（计划 ${t.plannedProgress}% / 实际 ${t.actualProgress}%，偏差 ${getDeviation(t)}%）`
      );
    });
    lines.push("");
  }
  if (overdue.length === 0 && blocked.length === 0 && deviationTasks.length === 0) {
    lines.push("- 当前无风险预警");
    lines.push("");
  }

  lines.push("## 下周计划", "");
  if (todo.length > 0) {
    todo.slice(0, 10).forEach((t) => {
      lines.push(`- ${t.title}（${t.assignee}，截止 ${t.dueDate}）`);
    });
    if (todo.length > 10) {
      lines.push(`- ...及其他 ${todo.length - 10} 个待办任务`);
    }
  } else {
    lines.push("- 暂无待办任务");
  }

  lines.push("", "---", `生成时间：${now}`);

  return lines.join("\n");
}

// ============================================
// 主组件
// ============================================

export default function AIProjectAssistant() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setTasks(loadTasks());
  }, []);

  useEffect(() => {
    loadData();
    // 初始欢迎消息
    setMessages([
      {
        id: `msg_${Date.now()}`,
        role: "assistant",
        content:
          "你好！我是 AI 项目助理。我可以帮你分析任务进度、识别风险、生成周报。请点击下方快捷指令开始，或直接输入问题。",
        timestamp: new Date().toISOString(),
      },
    ]);
  }, [loadData]);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ============================================
  // 数据分析汇总
  // ============================================
  const analysis = useMemo(() => {
    return {
      total: tasks.length,
      overdue: findOverdueTasks(tasks),
      blocked: findBlockedTasks(tasks),
      deviation: findDeviationTasks(tasks),
      conflicts: findResourceConflicts(tasks),
    };
  }, [tasks]);

  // ============================================
  // 添加消息
  // ============================================
  const addMessage = useCallback(
    (role: "user" | "assistant", content: string, suggestions?: AISuggestion[]) => {
      const msg: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        role,
        content,
        timestamp: new Date().toISOString(),
        suggestions,
      };
      setMessages((prev) => [...prev, msg]);
    },
    []
  );

  // ============================================
  // 处理快捷指令
  // ============================================
  const handleQuickCommand = useCallback(
    async (commandId: string) => {
      // 刷新数据
      const currentTasks = loadTasks();
      const currentProjects = loadCustomProjects();
      setTasks(currentTasks);

      const command = QUICK_COMMANDS.find((c) => c.id === commandId);
      if (!command) return;

      addMessage("user", `${command.label}`);

      setGenerating(true);
      // 模拟思考延迟
      await new Promise((r) => setTimeout(r, 400));

      switch (commandId) {
        case "plan":
          handleGeneratePlan(currentTasks, currentProjects);
          break;
        case "risk":
          handleRiskCheck(currentTasks);
          break;
        case "report":
          await handleGenerateReport(currentTasks, currentProjects);
          break;
        case "standup":
          handleDailyStandup(currentTasks);
          break;
      }
      setGenerating(false);
    },
    [addMessage]
  );

  // ============================================
  // 生成计划
  // ============================================
  const handleGeneratePlan = (currentTasks: Task[], currentProjects: Project[]) => {
    if (currentTasks.length === 0) {
      addMessage(
        "assistant",
        "当前没有任务数据。请先在「任务看板」中创建任务，我再为你生成项目计划。"
      );
      return;
    }

    // 按优先级和截止日排序
    const priorityOrder: Record<string, number> = {
      urgent: 0,
      high: 1,
      medium: 2,
      low: 3,
    };
    const sorted = [...currentTasks]
      .filter((t) => t.status !== "done")
      .sort((a, b) => {
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return a.dueDate.localeCompare(b.dueDate);
      });

    const suggestions: AISuggestion[] = sorted.slice(0, 5).map((t) => ({
      type: "plan",
      title: t.title,
      detail: `优先级：${t.priority} · 负责人：${t.assignee} · 截止：${t.dueDate} · 进度：${t.actualProgress}%`,
      severity: t.priority === "urgent" ? "danger" : t.priority === "high" ? "warning" : "info",
    }));

    const projectInfo =
      currentProjects.length > 0
        ? `关联项目 ${currentProjects.length} 个。`
        : "";

    addMessage(
      "assistant",
      `基于任务优先级和截止日期，我为你生成了执行计划。当前共有 ${sorted.length} 个未完成任务，${projectInfo}建议按以下顺序推进：\n\n${sorted
        .slice(0, 8)
        .map((t, i) => `${i + 1}. ${t.title}（${t.assignee}，${t.dueDate}）`)
        .join("\n")}${sorted.length > 8 ? `\n...及其他 ${sorted.length - 8} 个任务` : ""}`,
      suggestions
    );
  };

  // ============================================
  // 风险检查
  // ============================================
  const handleRiskCheck = (currentTasks: Task[]) => {
    if (currentTasks.length === 0) {
      addMessage(
        "assistant",
        "当前没有任务数据，无法进行风险检查。请先创建任务。"
      );
      return;
    }

    const overdue = findOverdueTasks(currentTasks);
    const blocked = findBlockedTasks(currentTasks);
    const deviation = findDeviationTasks(currentTasks);
    const conflicts = findResourceConflicts(currentTasks);

    const suggestions: AISuggestion[] = [];

    overdue.forEach((t) => {
      suggestions.push({
        type: "risk",
        title: `延期：${t.title}`,
        detail: `负责人：${t.assignee} · 截止：${t.dueDate} · 已过期`,
        severity: "danger",
      });
    });

    blocked.forEach((t) => {
      suggestions.push({
        type: "risk",
        title: `阻塞：${t.title}`,
        detail: `负责人：${t.assignee} · 截止：${t.dueDate} · 需排查阻塞原因`,
        severity: "danger",
      });
    });

    deviation.forEach((t) => {
      const dev = getDeviation(t);
      suggestions.push({
        type: "risk",
        title: `进度偏差：${t.title}`,
        detail: `计划进度 ${t.plannedProgress}% / 实际 ${t.actualProgress}% / 偏差 ${dev}%`,
        severity: dev > 20 ? "danger" : "warning",
      });
    });

    conflicts.forEach((taskList, assignee) => {
      suggestions.push({
        type: "risk",
        title: `资源冲突：${assignee}`,
        detail: `同时承担 ${taskList.length} 个高优先级任务：${taskList
          .map((t) => t.title)
          .join("、")}`,
        severity: "warning",
      });
    });

    const summary =
      suggestions.length === 0
        ? "✅ 风险检查完成，当前无风险项。所有任务进度正常。"
        : `⚠ 风险检查完成，发现 ${suggestions.length} 个风险项：\n\n` +
          `• 延期任务：${overdue.length}\n` +
          `• 阻塞任务：${blocked.length}\n` +
          `• 进度偏差 >10%：${deviation.length}\n` +
          `• 资源冲突：${conflicts.size}\n\n` +
          `建议优先处理延期和阻塞任务，调整资源分配。`;

    addMessage("assistant", summary, suggestions.length > 0 ? suggestions : undefined);
  };

  // ============================================
  // 生成周报
  // ============================================
  const handleGenerateReport = async (
    currentTasks: Task[],
    currentProjects: Project[]
  ) => {
    if (currentTasks.length === 0) {
      addMessage(
        "assistant",
        "当前没有任务数据，无法生成周报。请先创建任务。"
      );
      return;
    }

    const date = new Date().toISOString().slice(0, 10);
    const reportContent = buildWeeklyReport(currentTasks, currentProjects);
    const vaultPath = `${REPORT_VAULT_PATH}/项目周报-${date}.md`;

    try {
      const resp = await fetch("/vault-api/write", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: vaultPath, content: reportContent }),
      });

      if (resp.ok) {
        const json = await resp.json().catch(() => ({}));
        if (json?.success !== false) {
          addMessage(
            "assistant",
            `✅ 周报已生成并写入 Vault！\n\n📄 路径：${vaultPath}\n\n本周概况：\n• 总任务：${currentTasks.length}\n• 已完成：${currentTasks.filter((t) => t.status === "done").length}\n• 进行中：${currentTasks.filter((t) => t.status === "in_progress").length}\n• 阻塞：${currentTasks.filter((t) => t.status === "blocked").length}\n• 延期：${findOverdueTasks(currentTasks).length}`,
            [
              {
                type: "action",
                title: "周报已写入 Vault",
                detail: vaultPath,
                severity: "info",
              },
            ]
          );
          return;
        }
      }
      addMessage(
        "assistant",
        `⚠ 周报已生成，但写入 Vault 可能未成功（HTTP ${resp.status}）。\n\n周报内容预览：\n${reportContent.slice(0, 200)}...`
      );
    } catch (err) {
      addMessage(
        "assistant",
        `⚠ 写入 Vault 失败：${(err as Error).message}\n\n周报内容已生成，可手动保存。`
      );
    }
  };

  // ============================================
  // 每日站会
  // ============================================
  const handleDailyStandup = (currentTasks: Task[]) => {
    if (currentTasks.length === 0) {
      addMessage(
        "assistant",
        "当前没有任务数据，无法生成站会摘要。请先创建任务。"
      );
      return;
    }

    const done = currentTasks.filter((t) => t.status === "done");
    const inProgress = currentTasks.filter((t) => t.status === "in_progress");
    const blocked = currentTasks.filter((t) => t.status === "blocked");
    const todo = currentTasks.filter((t) => t.status === "todo");

    // 按负责人分组
    const byAssignee = new Map<string, { done: Task[]; inProgress: Task[]; blocked: Task[]; todo: Task[] }>();
    currentTasks.forEach((t) => {
      if (!t.assignee) return;
      if (!byAssignee.has(t.assignee)) {
        byAssignee.set(t.assignee, { done: [], inProgress: [], blocked: [], todo: [] });
      }
      const entry = byAssignee.get(t.assignee)!;
      if (t.status === "done") entry.done.push(t);
      else if (t.status === "in_progress") entry.inProgress.push(t);
      else if (t.status === "blocked") entry.blocked.push(t);
      else entry.todo.push(t);
    });

    let report = `📋 每日站会摘要\n\n`;
    report += `总计：${done.length} 已完成 / ${inProgress.length} 进行中 / ${blocked.length} 阻塞 / ${todo.length} 待办\n\n`;

    byAssignee.forEach((entry, assignee) => {
      report += `【${assignee}】\n`;
      if (entry.done.length > 0) {
        report += `  ✅ 已完成：${entry.done.map((t) => t.title).join("、")}\n`;
      }
      if (entry.inProgress.length > 0) {
        report += `  🔄 进行中：${entry.inProgress.map((t) => `${t.title}(${t.actualProgress}%)`).join("、")}\n`;
      }
      if (entry.blocked.length > 0) {
        report += `  🚫 阻塞：${entry.blocked.map((t) => t.title).join("、")}\n`;
      }
      report += "\n";
    });

    if (blocked.length > 0) {
      report += `⚠ 需要优先解决的阻塞：${blocked.map((t) => t.title).join("、")}`;
    }

    addMessage("assistant", report);
  };

  // ============================================
  // 处理用户输入
  // ============================================
  const handleSendInput = useCallback(async () => {
    if (!input.trim() || generating) return;
    const userInput = input.trim();
    setInput("");
    addMessage("user", userInput);

    setGenerating(true);
    await new Promise((r) => setTimeout(r, 400));

    const currentTasks = loadTasks();
    setTasks(currentTasks);

    // 简单关键词匹配
    const lower = userInput.toLowerCase();
    if (
      lower.includes("风险") ||
      lower.includes("预警") ||
      lower.includes("问题")
    ) {
      handleRiskCheck(currentTasks);
    } else if (
      lower.includes("周报") ||
      lower.includes("报告") ||
      lower.includes("总结")
    ) {
      const currentProjects = loadCustomProjects();
      await handleGenerateReport(currentTasks, currentProjects);
    } else if (
      lower.includes("站会") ||
      lower.includes("每日") ||
      lower.includes("standup")
    ) {
      handleDailyStandup(currentTasks);
    } else if (
      lower.includes("计划") ||
      lower.includes("排期") ||
      lower.includes("plan")
    ) {
      const currentProjects = loadCustomProjects();
      handleGeneratePlan(currentTasks, currentProjects);
    } else if (
      lower.includes("偏差") ||
      lower.includes("进度")
    ) {
      // 进度偏差分析
      const deviation = findDeviationTasks(currentTasks);
      if (deviation.length === 0) {
        addMessage("assistant", "✅ 当前所有任务进度正常，无偏差 >10% 的任务。");
      } else {
        const suggestions: AISuggestion[] = deviation.map((t) => ({
          type: "risk",
          title: `偏差 ${getDeviation(t)}%：${t.title}`,
          detail: `计划 ${t.plannedProgress}% / 实际 ${t.actualProgress}% / ${t.assignee}`,
          severity: getDeviation(t) > 20 ? "danger" : "warning",
        }));
        addMessage(
          "assistant",
          `发现 ${deviation.length} 个进度偏差 >10% 的任务，建议关注：`,
          suggestions
        );
      }
    } else {
      addMessage(
        "assistant",
        `我理解你的问题。当前项目中心共有 ${currentTasks.length} 个任务。你可以通过快捷指令使用以下功能：\n\n• 生成计划 — 按优先级排序任务\n• 风险检查 — 识别延期/阻塞/偏差\n• 生成周报 — 写入 Vault\n• 每日站会 — 按人汇总进度\n\n请点击对应按钮使用。`
      );
    }
    setGenerating(false);
  }, [input, generating, addMessage]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden flex flex-col"
      style={{ height: "100%" }}
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-violet-500/15 border border-violet-500/30">
            <Bot size={13} className="text-violet-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              AI 项目助理
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              AI Assistant · {tasks.length} 个任务 · {analysis.overdue.length} 延期 ·{" "}
              {analysis.blocked.length} 阻塞
            </p>
          </div>
        </div>

        <button
          onClick={loadData}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors"
          title="刷新数据"
        >
          <Sparkles size={11} className="text-violet-400" />
          <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
            刷新
          </span>
        </button>
      </div>

      {/* 快捷指令栏 */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
        {QUICK_COMMANDS.map((cmd) => {
          const CIcon = cmd.icon;
          return (
            <button
              key={cmd.id}
              onClick={() => handleQuickCommand(cmd.id)}
              disabled={generating}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50"
              style={{
                background: `${cmd.color}15`,
                borderColor: `${cmd.color}30`,
              }}
            >
              <CIcon size={10} style={{ color: cmd.color }} />
              <span
                className="text-[9px] font-mono uppercase tracking-wider"
                style={{ color: cmd.color }}
              >
                {cmd.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* 风险预警条 */}
      {(analysis.overdue.length > 0 || analysis.blocked.length > 0) && (
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-white/[0.04] bg-red-500/[0.03]">
          <AlertTriangle size={10} className="text-red-400" />
          <span className="text-[9px] font-mono text-red-400/80 uppercase tracking-wider">
            {analysis.overdue.length > 0 &&
              `${analysis.overdue.length} 个延期任务 · `}
            {analysis.blocked.length > 0 &&
              `${analysis.blocked.length} 个阻塞任务 · `}
            {analysis.deviation.length > 0 &&
              `${analysis.deviation.length} 个偏差>10%`}
            建议点击「风险检查」查看详情
          </span>
        </div>
      )}

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: "200px" }}>
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {generating && (
          <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500">
            <Loader2 size={12} className="animate-spin text-violet-400" />
            AI 正在分析...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入区 */}
      <div className="p-3 border-t border-white/[0.04] bg-white/[0.01]">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSendInput();
              }
            }}
            placeholder="输入问题，或点击上方快捷指令..."
            rows={1}
            className="flex-1 px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-violet-500/30 resize-none"
            style={{ maxHeight: "80px" }}
          />
          <button
            onClick={handleSendInput}
            disabled={!input.trim() || generating}
            className="flex items-center justify-center w-9 h-9 rounded-md bg-violet-500/20 border border-violet-500/30 text-violet-300 hover:bg-violet-500/30 transition-colors disabled:opacity-40"
          >
            {generating ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Send size={13} />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 消息气泡
// ============================================
function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      {/* 头像 */}
      <div
        className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center border ${
          isUser
            ? "bg-blue-500/15 border-blue-500/30"
            : "bg-violet-500/15 border-violet-500/30"
        }`}
      >
        {isUser ? (
          <User size={11} className="text-blue-400" />
        ) : (
          <Bot size={11} className="text-violet-400" />
        )}
      </div>

      {/* 消息内容 */}
      <div className={`flex-1 min-w-0 max-w-[85%]`}>
        <div
          className={`rounded-lg px-3 py-2 text-[11px] leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-blue-500/10 border border-blue-500/20 text-gray-200"
              : "bg-white/[0.03] border border-white/[0.06] text-gray-300"
          }`}
        >
          {message.content}
        </div>

        {/* 建议卡片 */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {message.suggestions.map((sug, idx) => (
              <SuggestionCard key={idx} suggestion={sug} />
            ))}
          </div>
        )}

        {/* 时间戳 */}
        <div className="mt-1 text-[8px] font-mono text-gray-700">
          {message.timestamp.slice(11, 16)}
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 建议卡片
// ============================================
function SuggestionCard({
  suggestion,
}: {
  suggestion: AISuggestion;
}) {
  const severityConfig = {
    info: { color: "#3B82F6", icon: Activity },
    warning: { color: "#F59E0B", icon: AlertTriangle },
    danger: { color: "#EF4444", icon: AlertOctagon },
  };
  const config = severityConfig[suggestion.severity];
  const SIcon = config.icon;

  return (
    <div
      className="flex items-start gap-2 rounded-md border p-2"
      style={{
        background: `${config.color}08`,
        borderColor: `${config.color}25`,
      }}
    >
      <SIcon size={11} style={{ color: config.color }} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div
          className="text-[10px] font-medium"
          style={{ color: config.color }}
        >
          {suggestion.title}
        </div>
        <div className="text-[9px] font-mono text-gray-500 mt-0.5">
          {suggestion.detail}
        </div>
      </div>
    </div>
  );
}
